import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDoc, useUpdateDoc } from '@/lib/queries';

interface Props {
  editable: boolean;
}

const SLUG = 'usage-guide';

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DocsCard({ editable }: Props) {
  const { data, isLoading } = useDoc(SLUG);
  const update = useUpdateDoc(SLUG);

  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setContent(data.content_md);
      setDirty(false);
    }
  }, [data]);

  const save = () => {
    update.mutate(
      { title, content_md: content },
      { onSuccess: () => setDirty(false) },
    );
  };

  return (
    <section className="panel">
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ink-800 px-6 py-4">
        <div>
          <div className="label">Documentación</div>
          <h2 className="mt-1 text-lg font-light text-ink-50">
            {data?.title ?? 'Guía de uso'}
          </h2>
          <div className="mt-1 text-xs text-ink-500">
            Última edición: {fmtDateTime(data?.updated_at ?? null)}
          </div>
        </div>
        {editable ? (
          <div className="flex items-center gap-2">
            <div className="flex border border-ink-800">
              <button
                type="button"
                onClick={() => setMode('preview')}
                className={`px-3 py-1 text-xs transition-colors ${
                  mode === 'preview' ? 'bg-ink-800 text-ink-100' : 'text-ink-400 hover:text-ink-200'
                }`}
              >
                Vista previa
              </button>
              <button
                type="button"
                onClick={() => setMode('edit')}
                className={`px-3 py-1 text-xs transition-colors ${
                  mode === 'edit' ? 'bg-ink-800 text-ink-100' : 'text-ink-400 hover:text-ink-200'
                }`}
              >
                Editar
              </button>
            </div>
            {dirty ? (
              <button
                type="button"
                disabled={update.isPending}
                onClick={save}
                className="border border-emerald-700 bg-emerald-900/30 px-3 py-1 text-xs text-emerald-300 transition-colors hover:bg-emerald-900/60 disabled:opacity-50"
              >
                {update.isPending ? 'Guardando…' : 'Guardar'}
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="px-6 py-5">
        {isLoading ? (
          <div className="py-10 text-center text-ink-500">Cargando…</div>
        ) : mode === 'edit' && editable ? (
          <div className="space-y-3">
            <div>
              <label className="label" htmlFor="doc-title">
                Título
              </label>
              <input
                id="doc-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setDirty(true);
                }}
                className="mt-1 w-full border border-ink-800 bg-ink-900 px-3 py-1.5 text-sm text-ink-100 focus:border-ink-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="label" htmlFor="doc-content">
                Contenido · Markdown
              </label>
              <textarea
                id="doc-content"
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  setDirty(true);
                }}
                rows={24}
                className="mt-1 w-full border border-ink-800 bg-ink-900 px-3 py-2 font-mono text-sm text-ink-100 focus:border-ink-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-ink-500">
              Admite Markdown estándar + GFM (tablas, listas de tareas, etc.).
            </p>
          </div>
        ) : (
          <article className="prose prose-invert max-w-none text-ink-200">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content || '_(vacío)_'}
            </ReactMarkdown>
          </article>
        )}
      </div>
    </section>
  );
}
